import type { ReactNode } from 'react';
import { Message } from '@gonfalon/messages/Message';
import type { EnvironmentReleasePlanClause } from '@gonfalon/openapi/types';
import {
  getEnvironmentReleasePlanQuery,
  getEnvironmentReleasePlansQuery,
} from '@gonfalon/rest-api/queries/environmentReleasePlans';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { MultiPhaseReleasePhaseDescription } from '../components/MultiPhaseReleasePhaseDescription/MultiPhaseReleasePhaseDescription';

export interface ReleasePlanReferenceDescriptionProps {
  readonly projectKey: string;
  readonly environmentKey: string;
  readonly planKey: string;
  readonly originalVariationId: string;
  readonly targetVariationId: string;
  readonly renderVariation: (variationId: string) => ReactNode;
  readonly renderClauses: (clauses: readonly EnvironmentReleasePlanClause[]) => ReactNode;
}

export function ReleasePlanReferenceDescription({
  projectKey,
  environmentKey,
  planKey,
  originalVariationId,
  targetVariationId,
  renderVariation,
  renderClauses,
}: ReleasePlanReferenceDescriptionProps) {
  const queryClient = useQueryClient();
  const cachedPlan = queryClient
    .getQueryData(
      getEnvironmentReleasePlansQuery({ projectKey, environmentKey, header: { 'LD-API-Version': 'beta' } }).queryKey,
    )
    ?.items.find((item) => item.key === planKey);
  const { data: fetchedPlan } = useQuery({
    ...getEnvironmentReleasePlanQuery({
      projectKey,
      environmentKey,
      releasePlanKey: planKey,
      header: { 'LD-API-Version': 'beta' },
    }),
    enabled: cachedPlan === undefined,
  });
  const plan = cachedPlan ?? fetchedPlan;

  const heading = (
    <Message id="releases.planReferenceSummary.startReleaseFromPlan" values={{ planName: plan?.name ?? planKey }} />
  );

  if (plan === undefined) {
    return heading;
  }

  const release = plan.progressiveRelease;
  const metrics = Object.entries(release.metrics ?? {}).map(([key, metric]) => ({
    key,
    autoRollback: metric.autoRollback,
  }));

  return (
    <div>
      {heading}
      <MultiPhaseReleasePhaseDescription
        phases={release.phases.map((phase) => ({
          description: phase.description,
          clauses: renderClauses(phase.clauses ?? []),
          stages: phase.steps.map((step) => ({
            rolloutWeight: step.allocation,
            monitoringWindowMilliseconds: step.durationMillis,
          })),
        }))}
        originalVariationId={originalVariationId}
        targetVariationId={targetVariationId}
        rolloutContextKind={release.randomizationUnit ?? ''}
        metrics={metrics}
        updateDefaultRuleOnCompletion={
          release.cleanupBehavior === undefined
            ? undefined
            : release.cleanupBehavior === 'remove_rules_and_update_default_rule'
        }
        advancementMode={release.advancementMode}
        renderVariation={renderVariation}
      />
    </div>
  );
}
